<template>
    <div class="flex items-center justify-between">
        <div class="w-full flex justify-between items-center sm:hidden">
            <PrimaryButton
                :disabled="currentPage == 1"
                @click="goToPage(currentPage - 1)"
                :size="smallSize ? 'sm' : 'md'"
                class="font-semibold shadow-sm flex-1"
            >
                <PhCaretLeft :class="['mx-1 size-6']" />
            </PrimaryButton>
            <p
                class="text-xl font-medium flex-1 text-center"
                v-text="`${$lang.get('Page')} ${currentPage}/${lastPage}`"
            />
            <PrimaryButton
                :disabled="currentPage >= lastPage"
                @click="goToPage(currentPage + 1)"
                :size="smallSize ? 'sm' : 'md'"
                class="font-semibold shadow-sm flex-1"
            >
                <PhCaretRight :class="['mx-1 size-6']" />
            </PrimaryButton>
        </div>
        <div class="hidden sm:flex sm:flex-1 items-center justify-between">
            <div>
                <p
                    :class="[
                        {
                            'md:text-base': !smallSize,
                        },
                        'text-sm text-text-secondary font-light',
                    ]"
                    v-html="rowsInfo"
                />
            </div>
            <div>
                <nav
                    :class="[
                        'grid isolate -space-x-px rounded-full bg-pagination-background text-pagination-text',
                        numberColumns,
                    ]"
                    aria-label="Pagination"
                >
                    <!-- Previous button -->
                    <button
                        :disabled="currentPage == 1"
                        @click="goToPage(currentPage - 1)"
                        class="disabled:cursor-not-allowed rounded-l-full ring-1 ring-inset ring-pagination-border enabled:hover:bg-pagination-hover enabled:hover:text-pagination-hover-text transition duration-button-hover focus:outline-offset-0 shadow-sm w-fit ml-auto"
                    >
                        <span class="sr-only" v-text="$lang.get('Previous')" />
                        <PhCaretLeft
                            :class="[
                                smallSize ? 'size-5' : 'size-6',
                                { 'opacity-20': currentPage == 1 },
                                'mx-1',
                            ]"
                        />
                    </button>

                    <!-- Page buttons -->
                    <template v-for="page in pages">
                        <button
                            v-if="page"
                            @click="goToPage(page)"
                            :disabled="currentPage == page"
                            :class="[
                                'disabled:bg-pagination-active disabled:text-pagination-active-text disabled:ring-pagination-active text-lg ring-1 ring-inset ring-pagination-border enabled:hover:bg-pagination-hover enabled:hover:text-pagination-hover-text transition duration-button-hover shadow-sm px-1.5',
                                {
                                    'text-sm': smallSize,
                                    'text-base': !smallSize,
                                },
                            ]"
                            v-text="page"
                        />

                        <span
                            v-else
                            class="inline-flex items-center justify-center text-sm font-semibold ring-1 ring-inset ring-pagination-border focus:outline-offset-0 select-none"
                            >...</span
                        >
                    </template>

                    <!-- Next button -->
                    <button
                        :disabled="currentPage >= lastPage"
                        @click="goToPage(currentPage + 1)"
                        :class="[
                            smallSize ? 'py-1' : 'py-1.5 px-0.5',
                            'disabled:cursor-not-allowed group rounded-r-full ring-1 ring-inset ring-pagination-border enabled:hover:bg-pagination-hover enabled:hover:text-pagination-hover-text transition duration-button-hover focus:outline-offset-0 shadow-sm w-fit',
                        ]"
                    >
                        <span class="sr-only" v-text="$lang.get('Next')" />
                        <PhCaretRight
                            :class="[
                                smallSize ? 'size-5' : 'size-6',
                                { 'opacity-20': currentPage >= lastPage },
                                'mx-1',
                            ]"
                        />
                    </button>
                </nav>
            </div>
        </div>
    </div>
</template>

<script>
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue'

export default {
    name: 'Pagination',
    components: {
        PhCaretLeft,
        PhCaretRight,
        PrimaryButton,
    },

    props: {
        currentPage: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
        },
        rowsCount: {
            type: Number,
            required: true,
        },
        totalCount: {
            type: Number,
            required: true,
        },
        lastPage: {
            type: Number,
            required: true,
        },
        smallSize: {
            type: Boolean,
            default: false,
        },
        isTable: {
            type: Boolean,
            default: true,
        },
        modelClass: {
            type: String,
            default: null,
        },
    },

    computed: {
        rowsInfo() {
            let from = (this.currentPage - 1) * this.perPage + 1
            let to = Math.min(this.currentPage * this.perPage, this.rowsCount)
            let filteredTotal = this.rowsCount
            let total = this.totalCount

            let string = ''

            if (this.isTable) {
                if (filteredTotal == 1) {
                    string = this.$lang.get('Showing :one row', {
                        one: `<span class='font-medium'>1</span>`,
                    })
                } else {
                    string = this.$lang.get(
                        'Showing row :from to :to of :filtered_total rows',
                        {
                            from: `<span class='font-medium'>${from}</span>`,
                            to: `<span class='font-medium'>${to}</span>`,
                            filtered_total: `<span class='font-medium'>${filteredTotal}</span>`,
                        }
                    )
                }
            } else {
                if (filteredTotal == 1) {
                    string = this.$lang.get('Showing :one :model', {
                        one: `<span class='font-medium'>1</span>`,
                        model: this.modelClass
                            ? this.$getBaseModelTranslation({
                                  modelName: this.modelClass,
                              })
                            : this.$lang.get('item'),
                    })
                } else {
                    string = this.$lang.get(
                        'Showing :from to :to of :filtered_total :models',
                        {
                            from: `<span class='font-medium'>${from}</span>`,
                            to: `<span class='font-medium'>${to}</span>`,
                            filtered_total: `<span class='font-medium'>${filteredTotal}</span>`,
                            models: this.modelClass
                                ? this.$getBaseModelTranslation({
                                      modelName: this.modelClass,
                                      wordClass: 'plural',
                                  })
                                : this.$lang.get('items'),
                        }
                    )
                }
            }

            if (total == filteredTotal) {
                return string
            }

            if (this.isTable) {
                string += this.$lang.get(' (filtered from :total rows)', {
                    total: `<span class='font-medium'>${total}</span>`,
                })
            } else {
                string += this.$lang.get(' (filtered from :total :models)', {
                    total: `<span class='font-medium'>${total}</span>`,
                    models: this.modelClass
                        ? this.$getBaseModelTranslation({
                              modelName: this.modelClass,
                              wordClass: 'plural',
                          })
                        : this.$lang.get('items'),
                })
            }

            return string
        },

        pages() {
            let pages = []
            let currentPage = this.currentPage
            let totalPages = this.lastPage

            // If there are less than or equal to 7 pages, simply return an array from 1 to totalPages
            if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i)
                }
            } else {
                // Always add the first page
                pages.push(1)

                // Determine the start and end of the middle section
                let middleStart = Math.max(2, currentPage - 1)
                let middleEnd = Math.min(totalPages - 1, currentPage + 1)

                // Adjust the start and end if they're too close to the first or last page
                if (middleStart <= 3) {
                    middleEnd = 5
                } else if (middleEnd >= totalPages - 2) {
                    middleStart = totalPages - 4
                }

                // Add null if the middle section doesn't start at the second page
                if (middleStart > 2) {
                    pages.push(null)
                }

                // Add the middle section
                for (let i = middleStart; i <= middleEnd; i++) {
                    pages.push(i)
                }

                // Add null if the middle section doesn't end at the second to last page
                if (middleEnd < totalPages - 1) {
                    pages.push(null)
                }

                // Always add the last page
                pages.push(totalPages)
            }

            return pages
        },

        numberColumns() {
            // 2 columns are for the previous and next buttons
            let columns = 2
            // Add the buttons for the pages
            columns += this.pages.length

            switch (columns) {
                case 3:
                    return 'grid-cols-3'
                case 4:
                    return 'grid-cols-4'
                case 5:
                    return 'grid-cols-5'
                case 6:
                    return 'grid-cols-6'
                case 7:
                    return 'grid-cols-7'
                case 8:
                    return 'grid-cols-8'
                case 9:
                    return 'grid-cols-9'
            }
        },
    },

    watch: {
        /**
         * If the number of pages changes and the current page is greater than the new number of pages,
         * change the current page to the first page.
         */
        lastPage(newVal) {
            if (this.currentPage > newVal) {
                this.$emit('ChangePage', 1)
            }
        },
    },

    methods: {
        goToPage(page) {
            this.$emit('ChangePage', page)
        },
    },
}
</script>
